import { Col, Form } from 'react-bootstrap'
import { Error } from '../../shared/simple/Error';
import { Label } from '../../shared/simple/Label';
import { TooltipIcon } from '../../shared/simple/TooltipIcon';
import { useFormContext } from 'react-hook-form'
import videoIconSource from '../../../assets/images/advance-tier/video-camera.svg'
import { t } from 'i18next';
import QRModal from './QRModal'
import { IoClose } from 'react-icons/io5'
import { DialogContent, DialogTitle, DialogActions, Dialog } from '@mui/material'
import { recorderStatus } from '../../../constants/form'
import IncomingVideoModal from './IncomingVideoModal'
import { fieldAttribute } from '../../../helpers/shared'
import { 
  SectionContainer, SectionFront,
  StyledDialogTitle, StyledMobileOption, FileIcon ,
  FileButton, InputRtc, FileVideo
} from '../../user/styled'
import { useVideos } from '../../../hooks/useVideos'
import { useDetect } from '../../../hooks/useDetect'
import { useQRPeer } from '../../../hooks/usePeer'
import { useIncomingVideoModal } from '../../../hooks/useIncomingVideoModal'
import { useQRModal } from '../../../hooks/useQRModal'
import { useRecord, useVideoModal } from '../../../hooks/useVideoInputRTC'
import { useFile }  from '../../../hooks/useFile'
import { useTextLanguage } from '../../../hooks/useShared'
import { useEdit, useEditFiles } from '../../../hooks/useEdit'
import { useLivenessDetection } from '../../../hooks/useLivenessDetection';
import InstructionsSection from './InstructionsSection';
import CustomVideoPlayer from './CustomVideoPlayer/CustomVideoPlayer';
import { useSwitchDesign } from '../../../hooks/useSwitchDesign';
import { Required } from '../../shared/Input/styled';

/**
 * @component
 * @param {Object} field - JSON Field from admin.
 * @param {String} type - String representing de file type; 'video' | 'audio'
 * @returns 
 */

const VideoInputRTC = ({ field, type, isFileUploadable }) => {
  const { description, languageText } = useTextLanguage();
  const { colSize } = useEdit(field, isFileUploadable)
  const { uniqueName, fileSizeLimit } = useFile(field, type);
  const { register, setValue, formState: {errors} } = useFormContext()
  useEditFiles(field)
  const { videoUrl, title } = useVideos()

  const {userPermission, videoMessage, stream} = useRecord()

  const { onPlay, setIsIntructionStarted, steps, stepIndex, resetLivenessDetection, isValid, isLivenessCompleted, isIntructionStarted } = useLivenessDetection()

  const { browserInfo, OS } = useDetect()
  /* Incoming video modal */
  const {
    openIncomingVideoModal,
    handleOpenIncomingVideoModal,
    handleCloseIncomingVideoModal,
    payload, setPayload
  } = useIncomingVideoModal(browserInfo)

  /* QRModals */
  const { isOpenQR, handleCloseQR, handleOpenQR } = useQRModal()

  /* Peer connection and url */
  const { id } = useQRPeer(isOpenQR, handleOpenIncomingVideoModal)

  const origin = window.location.origin
  const urlValue = `${origin}/AutovideoQRPage/${id}`

  /* Video Modal */
  const { 
    handleOpen,
    isOpen,
    status, setStatus,
    buttons,
    cancelRecording,
    handleClose,
    currentVideoSize
  } = useVideoModal(field, setValue, payload, unsetPeerRecording, browserInfo, OS, userPermission, setIsIntructionStarted, resetLivenessDetection, isValid, isLivenessCompleted)

  const handleOpenQRModal = () => {
    handleClose()
    handleOpenQR()
  }

  function unsetPeerRecording(){
    const videoPreview = fieldAttribute('data-video', `video-${field.unique_name}-preview`)
		const placeholderImage = fieldAttribute('data-image', `image-${uniqueName}`)
    const modalPreviewVideo = fieldAttribute('data-video', `video-${uniqueName}-modal-preview`)
    const videoIntructions = fieldAttribute('data-video', `video-instructions`)
    if(window.confirm(t('videoRecorder.confirmCancel'))){
      setPayload(null)
			videoPreview.src=''
			videoPreview.classList.add('d-none')
      modalPreviewVideo.classList.add('d-none')
			placeholderImage.classList.remove('d-none')
      videoIntructions.classList.remove('d-none')
			setValue(uniqueName, '')
			setStatus(recorderStatus.inactive)
		}

  }

  const { altErrorStyle, isNewDesign } = useSwitchDesign()
  return (
    <>
      {field && (
        <>
          {colSize?.includes('col-before') && (
              <Col xs='12' key={`col_before_${field.id}`}></Col>
          )}
          <Col
            className={ `mt-4 ${colSize}` }
            data-module={ `module-${field?.category?.field_category}` }
            data-name={uniqueName}
            id={ `group_field_${field.id}` }
            key={ field.id }
            xs={12}
          >
            <div className='mb-3'>
              <Label htmlFor={ field.name || field.id } isNewDesign={isNewDesign} >
                { field[description] }{ field.required && (
                  <Required className="text-danger">&nbsp;*</Required>) }
                { field?.[`tooltip${languageText}`] && (
                  <TooltipIcon 
                    tooltipText={ field[`tooltip${languageText}`] } 
                    color={isNewDesign && 'third'}
                  />) }
              </Label>

              <FileButton onClick={handleOpen}>
                <InputRtc 
                  type='file'
                  { ...register(uniqueName) }
                  data-action={ field?.action }
                  data-category={ `category-${field?.category?.field_category}` }
                  data-condition={ field?.parent_condition }
                  data-parent={ field?.parent }
                  data-type-field={ field?.data_type }
                  data-unique-name={ field.unique_name }
                  id={ field.id }
                  key={ field.id }
                  name={ uniqueName }
                />
                <FileIcon 
                  src={videoIconSource}
                  data-image={`image-${uniqueName}`}
                />
                <FileVideo
                  data-video={ `video-${field.unique_name}-preview` }
                  className="d-none"
                  id='file-video-preview'
                />
              </FileButton>
              <Form.Text muted>
                <Required className="text-danger">&nbsp;*&nbsp;</Required>
                <Label isNewDesign={isNewDesign} >
                  { t("files.fileSizeLimit", { size: fileSizeLimit }) }
                </Label>
              </Form.Text><br />
              {errors?.[uniqueName]?.message && (
                <Error 
                  message={ errors[uniqueName].message } 
                  altErrorMessageStyle={altErrorStyle}  
                />
              )}
            </div>
          </Col>
          {/* Modal where the video is gonna be recorded */}

          <Dialog onClose={handleClose} open={isOpen}
            sx={{
              '& .MuiDialog-paper': {
                // Media query for small devices
                
                maxWidth: {
                  xs: '90%',
                  md: 'max-content'
                }
              }
            }}
          > 
            {/* Cancel recording at any time (except inactive or received from QR) */}
            {(status !== recorderStatus.inactive && status !== recorderStatus.receivedFromQR) ? (
              <IoClose 
                onClick={cancelRecording}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  top: '1px',
                  right: '2px',
                  fontSize: '20px',
                  zIndex: 20
                }}
              />
            ) : null}
            {/* Cancel recording received from QR */}
            {(status === recorderStatus.receivedFromQR) ? (
              <IoClose 
                onClick={unsetPeerRecording}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  top: '1px',
                  right: '2px',
                  fontSize: '20px',
                  zIndex: 20
                }}
              />
            ) : null}

            <DialogTitle sx={{ position: 'relative' }}>
              <StyledDialogTitle className='text-muted'>
                <span>
                  {t('videoRecorder.callToAction')}
                </span>              
              </StyledDialogTitle>
            </DialogTitle>
            <DialogContent dividers 
              sx={{ 
                padding: '0px', 
                width: 'max-content', 
                maxWidth: {
                  xs: '100%',
                  md: 'max-content'
                }
              }}>

              <SectionContainer>
                <SectionFront>
                  <div
                    style={{
                      position: 'relative',
                    }}
                  >
                    {/* Video tag for the recording */}
                    <video className='d-none' data-video={ `video-${uniqueName}` } autoPlay muted playsInline
                      onLoadedMetadata={onPlay}
                      width={640} height={480}
                      id='video-record-rtc'
                    /> 
                    <canvas id='canvas-overlay'></canvas>
                    {(status !== recorderStatus.inactive && status !== recorderStatus.stopped && status !== recorderStatus.receivedFromQR)
                      ? (
                          <div className='badge rounded-pill bg-dark position-absolute user-select-none'
                            style={{
                              bottom: '8px',
                              left: '2px'
                            }}
                          >
                            {(parseInt(currentVideoSize)/ Math.pow(1024, 2)).toFixed(1)}/{fileSizeLimit}MB
                          </div>
                        )
                      : null}
                    {/* Video tag for the ACTUALLY PLAYABLE preview */}
                    <CustomVideoPlayer uniqueName={uniqueName} />
                    <iframe data-video='video-instructions' height={'250px'} src={videoUrl} title={title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
                  </div>
                    
                  {status !== recorderStatus.receivedFromQR ?
                  (
                    <InstructionsSection 
                      message={videoMessage.message} 
                      steps={steps}  
                      stepIndex={stepIndex}
                      isLivenessCompleted={isLivenessCompleted}
                      isIntructionStarted={isIntructionStarted}
                    />
                  ): null}
                  <DialogActions sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingInline: '40px', paddingBlock: '0px' }}>
                    {buttons}
                    {/* Don't show mobile option if the video is recording, paused, already recorded (stopped) or received from QR */}
                    {(status !== recorderStatus.stopped && status !== recorderStatus.recording && status !== recorderStatus.paused && status !== recorderStatus.receivedFromQR) ? (
                    <StyledMobileOption onClick={handleOpenQRModal}>
                        {t('videoRecorder.mobileOption')}
                    </StyledMobileOption>
                    ) : null}
                  </DialogActions>
                </SectionFront>
              </SectionContainer>
  
            </DialogContent>
      
            {/* Camera permission denied previously? ↓ */}
            {/* {(userPermission === 'denied') ?
              (<>
                <DialogTitle sx={{ paddingInline: '40px' }}>
                  Oops
                </DialogTitle>
                <DialogContent dividers sx={{ padding: '0px'}}>
                  <div className='alert alert-warning m-1' role='alert' dangerouslySetInnerHTML={{__html: t('videoRecorder.permissionsDeniedPreviously')}}>
                    
                  </div>
                </DialogContent>
              </>) : null
            } */}
          </Dialog>
          {/* Modal for the QR the be shown */}
          <QRModal
            open={isOpenQR} urlValue={urlValue} id={id}
            handleCloseQR={handleCloseQR} handleOpen={handleOpen}
            browserInfo={browserInfo}
          />
          {/* Modal for the video to be received and accepted */}
          <IncomingVideoModal 
            open={openIncomingVideoModal}
            onCloseIncomingVideoModal={handleCloseIncomingVideoModal}
            onCloseQR={handleCloseQR}
            payload={payload}
            field={field} setValue={setValue}
            setStatus={setStatus} id={id}
          />
        </>
      )}
    </>
  )
}

export default VideoInputRTC